// Actions / Follow-up — incident-sourced corrective / preventative /
// investigation actions, linked to the Actions module.
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ListChecks, Loader2, AlertTriangle, Clock, CheckCircle2, Inbox, Ban, ExternalLink,
} from "lucide-react";
import { api } from "@/lib/api";
import Pill from "@/components/Pill";
import { FilterBar, ChartCard, Empty } from "@/pages/safety/metrics/shared";

const STATUS_TONE = { Open: "amber", "In progress": "blue", Blocked: "red", Submitted: "purple", Complete: "green" };
const KIND_TONE = { Corrective: "accent", Preventative: "blue", Investigation: "purple" };

export default function IncidentActions({ filters, onChange, clients, buildings, projects, meta, isPortal }) {
    const [rows, setRows] = useState(null);
    const [statusFilter, setStatusFilter] = useState("All");
    const navigate = useNavigate();

    useEffect(() => {
        const params = {};
        if (filters.client_id) params.client_id = filters.client_id;
        if (filters.building_id) params.building_id = filters.building_id;
        setRows(null);
        api.get("/incidents/actions", { params }).then((r) => setRows(r.data)).catch(() => setRows([]));
    }, [filters.client_id, filters.building_id]);

    const today = new Date().toISOString().slice(0, 10);
    const stats = useMemo(() => {
        const s = { open: 0, in_progress: 0, blocked: 0, submitted: 0, complete: 0, overdue: 0, total: 0 };
        (rows || []).forEach((a) => {
            s.total++;
            const st = a.status || "Open";
            if (st === "Open") s.open++;
            else if (st === "In progress") s.in_progress++;
            else if (st === "Blocked") s.blocked++;
            else if (st === "Submitted") s.submitted++;
            else if (st === "Complete") s.complete++;
            if (st !== "Complete" && a.due_date && a.due_date < today) s.overdue++;
        });
        return s;
    }, [rows, today]);

    const filtered = (rows || []).filter((a) => {
        if (statusFilter === "All") return true;
        if (statusFilter === "Overdue") return a.status !== "Complete" && a.due_date && a.due_date < today;
        return (a.status || "Open") === statusFilter;
    });

    const clientMap = Object.fromEntries(clients.map((c) => [c.id, c]));
    const buildingMap = Object.fromEntries(buildings.map((b) => [b.id, b]));

    const CARDS = [
        { id: "Open", label: "Open", icon: Inbox, tone: "amber", value: stats.open },
        { id: "In progress", label: "In progress", icon: Clock, tone: "blue", value: stats.in_progress },
        { id: "Blocked", label: "Awaiting / blocked", icon: Ban, tone: "red", value: stats.blocked },
        { id: "Submitted", label: "Under review", icon: ListChecks, tone: "purple", value: stats.submitted },
        { id: "Complete", label: "Closed", icon: CheckCircle2, tone: "green", value: stats.complete },
        { id: "Overdue", label: "Overdue", icon: AlertTriangle, tone: "red", value: stats.overdue },
    ];

    return (
        <div data-testid="incident-actions-view" className="space-y-5">
            <FilterBar filters={filters} onChange={onChange} clients={clients} buildings={buildings}
                projects={projects} meta={meta} isPortal={isPortal} />

            {/* Status summary — clickable to filter the table */}
            <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(150px,1fr))" }}>
                {CARDS.map((c) => {
                    const active = statusFilter === c.id;
                    const tones = { amber: "#C68A1C", blue: "#2A5DB0", red: "#B33A2E", purple: "#6B4E9E", green: "#3F7D3A" };
                    return (
                        <button key={c.id} data-testid={`action-stat-${c.id}`}
                            onClick={() => setStatusFilter(active ? "All" : c.id)}
                            className={`bg-white border rounded-[11px] p-3.5 text-left transition ${active ? "border-[var(--brand-accent,#C75A1B)] ring-1 ring-[var(--brand-accent,#C75A1B)]" : "border-[#E5E0DA] hover:border-[#D5CEC6]"}`}>
                            <div className="flex items-center justify-between mb-2">
                                <c.icon size={16} style={{ color: tones[c.tone] }} />
                            </div>
                            <div className="font-display font-bold text-[24px] leading-none text-[#1F1F1F]">{c.value}</div>
                            <div className="text-[11px] mt-1.5 font-medium text-[#6F6A64]">{c.label}</div>
                        </button>
                    );
                })}
            </div>

            <ChartCard testid="incident-actions-table-card"
                title={statusFilter === "All" ? "All follow-up actions" : `${statusFilter} actions`}
                subtitle={`${filtered.length} of ${stats.total} · corrective, preventative & investigation actions linked to incidents`}
                right={statusFilter !== "All" ? (
                    <button onClick={() => setStatusFilter("All")} className="text-[12px] text-[var(--brand-accent,#C75A1B)] font-semibold">Clear</button>
                ) : null}>
                {rows == null ? (
                    <div className="text-center py-10 text-[#9A968F]"><Loader2 size={16} className="inline animate-spin mr-2" />Loading…</div>
                ) : filtered.length === 0 ? (
                    <Empty label="No follow-up actions match." />
                ) : (
                    <div className="overflow-x-auto -mx-5">
                        <table className="w-full text-[12.7px]">
                            <thead><tr className="bg-[#F6F4F0] text-[10.5px] uppercase tracking-wider text-[#6F6A64]">
                                <th className="text-left px-5 py-2.5 font-bold">Action</th>
                                <th className="text-left px-3 py-2.5 font-bold">Kind</th>
                                <th className="text-left px-3 py-2.5 font-bold">Incident</th>
                                <th className="text-left px-3 py-2.5 font-bold">Assignee</th>
                                <th className="text-left px-3 py-2.5 font-bold">Due</th>
                                <th className="text-left px-3 py-2.5 font-bold">Status</th>
                                <th className="px-5 py-2.5"></th>
                            </tr></thead>
                            <tbody>
                                {filtered.map((a) => {
                                    const overdue = a.status !== "Complete" && a.due_date && a.due_date < today;
                                    return (
                                        <tr key={a.id} data-testid={`incident-action-${a.id}`} className="border-t border-[#F0EDE8] hover:bg-[#FAF8F5] cursor-pointer"
                                            onClick={() => navigate("/app/actions")}>
                                            <td className="px-5 py-2.5 font-semibold text-[#1F1F1F] max-w-[280px]">{a.title}</td>
                                            <td className="px-3 py-2.5">{a.kind ? <Pill tone={KIND_TONE[a.kind] || "neutral"}>{a.kind}</Pill> : <span className="text-[#9A968F]">—</span>}</td>
                                            <td className="px-3 py-2.5 font-mono text-[11px] text-[#6F6A64]">{a.source_ref || "—"}</td>
                                            <td className="px-3 py-2.5 text-[#6F6A64]">{a.assignee_name || "—"}</td>
                                            <td className="px-3 py-2.5" style={{ color: overdue ? "#B33A2E" : "#6F6A64" }}>
                                                {a.due_date || "—"}{overdue && <AlertTriangle size={11} className="inline ml-1" />}
                                            </td>
                                            <td className="px-3 py-2.5"><Pill tone={STATUS_TONE[a.status] || "neutral"}>{a.status}</Pill></td>
                                            <td className="px-5 py-2.5 text-right"><ExternalLink size={13} className="text-[#C0BBB3] inline" /></td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </ChartCard>
        </div>
    );
}
